import { useTranslation } from 'react-i18next';

import { THEME_PREFS, type ThemePref } from './theme';
import { useTheme } from './useTheme';

/**
 * Slice 23 (admin half) — the Appearance control.
 *
 * 🔴 ADMIN HAS NO SETTINGS SCREEN. Canvas 5f/6i draw a "Settings" nav entry, but
 * `AdminNav` deliberately does not render it — it points at nothing that exists,
 * and this codebase ships no control for a feature that does not exist. So there
 * is no natural Settings home for a theme toggle (the web app puts its
 * `AppearancePanel` on Settings screen 1r). The next-most-natural home for a
 * per-device preference is the account block at the foot of the shell rail,
 * beside Sign out — where account-scoped chrome already lives. This is a compact
 * segmented Light / Dark / Auto control, the same shape and store as the web
 * app's panel, rendered there.
 *
 * Indigo, like the rest of admin — the selected segment is `bg-raad-primary
 * text-raad-onprimary`, every token declared in `admin/src/styles.css`.
 */
export function AppearanceControl() {
  const { t } = useTranslation();
  const { pref, setPref } = useTheme();

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 lg:mt-2 lg:px-2">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-raad-muted">
        {t('appearance.label')}
      </span>
      <div
        role="radiogroup"
        aria-label={t('appearance.label')}
        className="inline-flex rounded-full border border-raad-border bg-raad-input p-0.5"
      >
        {THEME_PREFS.map((option) => {
          const selected = pref === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setPref(option as ThemePref)}
              className={[
                'min-h-8 rounded-full px-2.5 text-[11px] font-semibold transition-colors',
                selected
                  ? 'bg-raad-primary text-raad-onprimary'
                  : 'text-raad-muted hover:text-raad-text',
              ].join(' ')}
            >
              {t(`appearance.${option}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
